import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {ProgressCircle} from '@github-ui/progress-circle'
import {Link, Token} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {graphql, useFragment} from 'react-relay'

import type {IssueItemSubIssuesSummary$key} from './__generated__/IssueItemSubIssuesSummary.graphql'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import styles from './IssueItemSubIssuesSummary.module.css'

type Props = {
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  issueId: string
  link: string
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const subIssuesFragment = graphql`
  fragment IssueItemSubIssuesSummary on Issue {
    subIssuesSummary {
      completed
      percentCompleted
      total
    }
  }
`

export function IssueItemSubIssuesSummary({listItemMetadata, issueId, link}: Props) {
  if (!listItemMetadata) return null

  const issueNode = listItemMetadata.nodes?.find(node => node?.id === issueId)

  if (!issueNode) return null

  return <SubIssuesSummaryInternal summaryKey={issueNode} link={link} />
}

type InternalProps = {
  summaryKey?: IssueItemSubIssuesSummary$key
  link: string
}

function SubIssuesSummaryInternal({summaryKey, link}: InternalProps) {
  const data = useFragment(subIssuesFragment, summaryKey)
  if (!data?.subIssuesSummary) return null

  const {total, completed, percentCompleted} = data.subIssuesSummary
  if (total === 0) return null

  return (
    <ListItemTrailingBadge title="sub-issues summary">
      <Tooltip text={`${percentCompleted}% completed`} type="description">
        <Link href={link} onKeyDown={e => e.stopPropagation()}>
          <Token
            className={styles.token}
            leadingVisual={() => (
              <ProgressCircle percentCompleted={percentCompleted} size={14} svgClassName={styles.progressCircle} />
            )}
            text={<span>{`${completed} / ${total}`}</span>}
          />
        </Link>
      </Tooltip>
    </ListItemTrailingBadge>
  )
}
